import { useCallback, useState } from 'react';

import { sendMessage } from '../api/client';
import type { ChatSummary } from '../api/types';
import { useLocale } from '../i18n/LocaleContext';
import { humanizeError } from '../util/humanizeError';
import { encodeContactCardPayload } from '../util/chatPayload';
import { notifyAction } from '../util/confirmDialog';

/**
 * Кого предлагаем отправить карточкой. Всё, кроме имени и идентификатора, необязательно:
 * карточка собирается из публичного профиля, а он вправе не отдать ни аватара, ни описания.
 */
export type ShareContactTarget = {
  userId: string;
  displayName: string;
  publicHandle?: string | null;
  hasAvatar?: boolean;
  avatarRevision?: number;
  avatarShape?: string | null;
  bio?: string | null;
  statusText?: string | null;
};

/**
 * «Поделиться контактом» (стори 5.11): выбранный человек, пикер чата и сама отправка.
 *
 * <p>Один и тот же поток открывается с двух экранов — из дружины в списке чатов и из карточки
 * собеседника внутри переписки, — и до этого был написан в обоих: одинаковый тип состояния,
 * одинаковая перекладка профиля в карточку и двадцать четыре строки отправки, совпадавшие
 * дословно. Разъехавшись, две копии отправили бы разную карточку из двух мест, где человек
 * делает одно и то же действие.
 *
 * <p>Цель сбрасывается до {@code await}, а не после: пикер закрывается сразу по нажатию, иначе
 * второе нажатие по соседнему чату успевает отправить карточку дважды.
 */
export function useShareContact() {
  const { t } = useLocale();
  const [target, setTarget] = useState<ShareContactTarget | null>(null);

  const sendToChat = useCallback(
    async (chat: ChatSummary) => {
      if (!target) return;
      setTarget(null);
      try {
        await sendMessage(
          chat.id,
          encodeContactCardPayload({
            userId: target.userId,
            displayName: target.displayName,
            publicHandle: target.publicHandle,
            hasAvatar: target.hasAvatar,
            avatarRevision: target.avatarRevision,
            avatarShape: target.avatarShape,
            bio: target.bio,
            statusText: target.statusText,
          }),
        );
        void notifyAction({ title: t('contactShareSent'), okText: t('dialogOk') });
      } catch (e) {
        void notifyAction({
          title: t('errorTitle'),
          body: humanizeError(e, t('contactShareFailed')),
          okText: t('dialogOk'),
        });
      }
    },
    [target, t],
  );

  return { target, setTarget, sendToChat };
}

/**
 * Карточка для отправки из публичного профиля.
 *
 * <p>Отдельной функцией, потому что оба экрана строят её в одном и том же месте — в ответе
 * {@code getUserPublicProfile}, — и оба обязаны брать имя из профиля, а не из строки списка:
 * в списке оно могло устареть.
 */
export function shareTargetFromProfile(
  profile: {
    id: string;
    displayName: string;
    publicHandle?: string | null;
    hasAvatar?: boolean;
    avatarRevision?: number | null;
    avatarShape?: string | null;
    bio?: string | null;
    statusText?: string | null;
  },
  fallbackDisplayName: string,
): ShareContactTarget {
  return {
    userId: profile.id,
    displayName: profile.displayName || fallbackDisplayName,
    publicHandle: profile.publicHandle,
    hasAvatar: profile.hasAvatar,
    avatarRevision: profile.avatarRevision ?? 0,
    avatarShape: profile.avatarShape,
    bio: profile.bio ?? null,
    statusText: profile.statusText ?? null,
  };
}
